import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useEditCanvasStore } from '@air/feature-edit-canvas';

import { usePendingCanvasEditStore } from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useDiscardDraftCanvas } from '~/hooks/canvases/useDiscardDraftCanvas';
import { isPrivateCanvasPagePathname } from '~/utils/PathUtils';
import { subscribeZustandPersistHydration } from '~/utils/subscribeZustandPersistHydration';

type UseClearUi2026SplitPanelCanvasOptions = {
  enabled: boolean;
};

type ClearSplitPanelChromeOptions = {
  keepSelectedCanvasId?: boolean;
};

type BooleanRef = {
  readonly current: boolean;
};

/** Biome cannot see writes to `ref.current` across effect runs, so it treats the initial value as constant. */
const isRefTrue = (ref: BooleanRef) => ref.current;

const clearSplitPanelChrome = ({ keepSelectedCanvasId = false }: ClearSplitPanelChromeOptions = {}) => {
  const { setBoardViewCollapsed, setFullscreen, setOpen, setSelectedCanvasId } = useWorkspaceRightPanelStore.getState();

  setOpen(false);
  setFullscreen(false);
  setBoardViewCollapsed(false);

  if (!keepSelectedCanvasId) {
    setSelectedCanvasId(null);
  }
};

/**
 * The workspace no longer hosts a canvas in the right panel. Drop rehydrated panel
 * chrome (and leftover selection on non-canvas routes) so the header and agent panel
 * stay in the full-page layout. Mount once from the private workspace shell.
 */
export const useClearUi2026SplitPanelCanvas = ({ enabled }: UseClearUi2026SplitPanelCanvasOptions) => {
  const { isReady: isRouterReady, pathname } = useRouter();
  const discardDraftCanvas = useDiscardDraftCanvas();
  const hasRunSplitPanelClearRef = useRef<boolean>(false);
  const isPrivateCanvasPageRoute = isPrivateCanvasPagePathname(pathname);
  const prevIsPrivateCanvasPageRouteRef = useRef(isPrivateCanvasPageRoute);
  const keepSelectedCanvasIdOnHydrationRef = useRef(isPrivateCanvasPageRoute);
  keepSelectedCanvasIdOnHydrationRef.current = isPrivateCanvasPageRoute;

  useEffect(() => {
    if (!enabled || !isRouterReady) {
      return;
    }

    if (isRefTrue(hasRunSplitPanelClearRef)) {
      return;
    }

    const clearSplitPanelPersist = () => {
      hasRunSplitPanelClearRef.current = true;
      clearSplitPanelChrome({ keepSelectedCanvasId: keepSelectedCanvasIdOnHydrationRef.current });
    };

    return subscribeZustandPersistHydration(useWorkspaceRightPanelStore.persist, clearSplitPanelPersist);
  }, [enabled, isRouterReady]);

  useEffect(() => {
    const wasPrivateCanvasPage = prevIsPrivateCanvasPageRouteRef.current;
    prevIsPrivateCanvasPageRouteRef.current = isPrivateCanvasPageRoute;

    if (!enabled || !isRouterReady || !wasPrivateCanvasPage || isPrivateCanvasPageRoute) {
      return;
    }

    const { committingDraft, draftCanvas } = useWorkspaceRightPanelStore.getState();

    if (committingDraft) {
      useWorkspaceRightPanelStore.getState().setSkipPostCommitNavigation(true);
    }

    if (draftCanvas && !committingDraft) {
      discardDraftCanvas();
    }

    if (!committingDraft) {
      const returnTo = useWorkspaceRightPanelStore.getState().canvasReturnTo;
      const clearAfterAssetModalReturn = returnTo?.includes('/c/');

      const finishCanvasExit = () => {
        usePendingCanvasEditStore.getState().clearPending();
        clearSplitPanelChrome();
        useWorkspaceRightPanelStore.getState().setCanvasReturnTo(null);
        // A cold ui2026 deep link can keep the panel at open: false, selectedCanvasId: null,
        // so deleting the last node, closing, and reopening that canvas never clears this latch
        // and leaves the new visit without the intro. This hook already owns the dedicated-page
        // exit, so it also needs to reset the visit latch.
        useEditCanvasStore.getState().resetHasEverHadNodesVisit();
      };

      if (clearAfterAssetModalReturn) {
        requestAnimationFrame(() => {
          requestAnimationFrame(finishCanvasExit);
        });
      } else {
        finishCanvasExit();
      }
    }
  }, [discardDraftCanvas, enabled, isPrivateCanvasPageRoute, isRouterReady]);
};
